import { useEffect, useRef } from 'react';
import { CalendarPlus, Check, MessageSquareWarning } from 'lucide-react';
import { coachingCheckpoints, coachingHintKey, fillCoachingText, type CoachingHint } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Button, Eyebrow, Icon } from '../../ds/components';
import { useLiveTranscript } from './useLiveTranscript';
import styles from './LiveTranscript.module.css';

/**
 * „Live-Transkript" (D-221): the running call's transcript (speaker „Sie" / the contact's first name, auto-scroll),
 * the live hints of the server's matcher — objections with the suggested answers, the script checkpoints as a
 * checklist, the booking intent with the shortcut „Termin jetzt buchen" — while Twilio transcribes the call.
 */

export const LIVE_TRANSCRIPT_EMPTY = 'Das Transkript erscheint, sobald gesprochen wird.';

/** Distance from the bottom (px) within which new lines keep the list scrolled to the end. */
const STICK_TO_BOTTOM = 48;

export interface LiveTranscriptPanelProps {
  callId: string | null;
  /** „Max Mustermann" — the first word labels the contact's lines. */
  contactName: string;
  /** Jumps to the booking of the screen; without it the booking-intent card has no button. */
  onBook?: (() => void) | undefined;
  /** The record's call editor: no checklist, a lower transcript. */
  compact?: boolean | undefined;
}

export function LiveTranscriptPanel({
  callId,
  contactName,
  onBook,
  compact = false,
}: LiveTranscriptPanelProps) {
  const { segments, hints } = useLiveTranscript(callId);
  const firstName = contactName.trim().split(/\s+/)[0] || 'Kontakt';
  const listRef = useRef<HTMLOListElement>(null);
  const stick = useRef(true);

  useEffect(() => {
    const list = listRef.current;
    if (list !== null && stick.current) list.scrollTop = list.scrollHeight;
  }, [segments.length]);

  const fired = new Set(hints.map((hint) => hint.key));
  const objections = hints.filter((hint) => hint.kind === 'objection').reverse();
  const bookingIntent = hints.find((hint) => hint.kind === 'booking_intent') ?? null;

  return (
    <section className={styles.card} aria-labelledby="live-transcript-title">
      <div className={styles.head}>
        <Eyebrow>
          <span id="live-transcript-title">Live-Transkript</span>
        </Eyebrow>
      </div>

      {bookingIntent !== null ? <BookingIntent hint={bookingIntent} onBook={onBook} /> : null}
      {objections.map((hint) => (
        <Objection key={hint.key} hint={hint} />
      ))}

      {compact ? null : (
        <div className={styles.checklist}>
          <Eyebrow as="div">Gesprächsleitfaden</Eyebrow>
          <ul className={styles.checks}>
            {coachingCheckpoints.map((checkpoint) => {
              const done = fired.has(coachingHintKey('checkpoint', checkpoint.key));
              return (
                <li key={checkpoint.key} className={clsx(styles.check, done && styles.checkDone)}>
                  <span className={styles.tick} aria-hidden="true">
                    {done ? <Icon icon={Check} size={16} /> : null}
                  </span>
                  <span>{fillCoachingText(checkpoint.label, { firstName, topic: '' })}</span>
                  {done ? <span className={styles.srOnly}> (erledigt)</span> : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {segments.length === 0 ? (
        <p className={styles.empty}>{LIVE_TRANSCRIPT_EMPTY}</p>
      ) : (
        <ol
          ref={listRef}
          className={clsx(styles.lines, compact && styles.linesCompact)}
          role="log"
          aria-label="Transkript"
          onScroll={(event) => {
            const list = event.currentTarget;
            stick.current = list.scrollHeight - list.scrollTop - list.clientHeight < STICK_TO_BOTTOM;
          }}
        >
          {segments.map((segment) => (
            <li
              key={segment.seq}
              className={clsx(styles.line, segment.speaker === 'setter' && styles.lineSetter)}
            >
              <span className={styles.speaker}>{segment.speaker === 'setter' ? 'Sie' : firstName}</span>
              <span className={styles.text}>{segment.text}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function Objection({ hint }: { hint: CoachingHint }) {
  const answers = hint.text.split('\n\n').filter((answer) => answer.trim() !== '');
  return (
    <div className={styles.hint} role="status">
      <div className={styles.hintHead}>
        <Icon icon={MessageSquareWarning} size={16} />
        <span className={styles.hintTitle}>{hint.title}</span>
      </div>
      <ul className={styles.answers}>
        {answers.map((answer) => (
          <li key={answer}>{answer}</li>
        ))}
      </ul>
    </div>
  );
}

function BookingIntent({ hint, onBook }: { hint: CoachingHint; onBook?: (() => void) | undefined }) {
  return (
    <div className={clsx(styles.hint, styles.hintBooking)} role="status">
      <div className={styles.hintHead}>
        <Icon icon={CalendarPlus} size={16} />
        <span className={styles.hintTitle}>{hint.title}</span>
      </div>
      <p className={styles.hintText}>{hint.text}</p>
      {onBook ? (
        <Button size="small" icon={<Icon icon={CalendarPlus} size={16} />} onClick={onBook}>
          Termin jetzt buchen
        </Button>
      ) : null}
    </div>
  );
}
